import React, { useState } from 'react'
import { makeStyles, ActionButton, Alert, IconButton, InputField, Typography, useMediaQuery } from '@paintscout/ui'
import { EstimatorSelect, useUser } from '@paintscout/ui-business'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import type { Deal } from '@paintscout/api'
import { addAuth0Prefix } from '@paintscout/util'
import type { DashboardEstimator } from '@paintscout/util/builder'
import { useConnection } from 'react-detect-offline'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import { useSnackbar } from 'notistack'
import CreateIcon from '@material-ui/icons/Create'
import CloseIcon from '@material-ui/icons/Close'
import CheckIcon from '@material-ui/icons/Check'
import ContactBar from '../../people/ContactBar/ContactBar'

const SPACE_BETWEEN = 'space-between'

const useStyles = makeStyles<Theme, DealHeaderProps>(
  (theme) => ({
    root: {},
    ownedHeaderLg: {
      display: 'flex',
      justifyContent: SPACE_BETWEEN,
      alignItems: 'flex-start'
    },
    actionContainer: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      justifyContent: SPACE_BETWEEN
    },
    editIcon: {
      color: theme.palette.grey[400],
      marginLeft: theme.spacing(2)
    },
    ownedHeaderSm: {
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
      alignItems: 'center'
    },
    headerContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      '& > h3': {
        fontSize: theme.typography.h4.fontSize
      },
      '&:hover': {
        // target the nested icon, even if not directly nested
        cursor: 'pointer',
        '& svg': {
          color: theme.palette.grey[600]
        }
      }
    },
    editHeader: {
      display: 'flex',
      justifyContent: SPACE_BETWEEN,
      alignItems: 'center'
    },
    editIconMargin: {
      marginRight: `-${theme.spacing(4)}px`
    },
    ownedDropdown: {
      display: 'flex',
      alignItems: 'center',
      width: 'max-content'
    },
    profileImage: {
      borderRadius: '50%',
      objectFit: 'cover',
      fontSize: '1.5em',
      width: '1.2em',
      height: '1.2em',
      margin: theme.spacing(),
      marginLeft: 0
    },
    actionButton: {
      display: 'flex',
      alignItems: 'flex-end',
      flexDirection: 'column',
      marginTop: `${theme.spacing(0.75)}px`,
      '@media print': {
        display: 'none'
      }
    },
    backLink: {
      color: theme.palette.grey[700],
      '&:hover': {
        color: theme.palette.primary.main
      }
    },
    contactBar: {
      display: 'flex',
      justifyContent: SPACE_BETWEEN,
      alignItems: 'center',
      width: '100%',
      marginTop: theme.spacing(2),
      '& > div': {
        display: 'flex',
        alignItems: 'center'
      }
    },
    nameDiv: {
      width: '100%',
      display: 'flex',
      // flexDirection: 'column'
      alignItems: 'center',
      '&:hover': {
        cursor: 'pointer',
        '& svg': {
          visibility: 'visible'
        }
      }
    },
    nameInputDiv: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      maxWidth: 530
    },
    successButton: {
      color: theme.palette.success.main
    },
    cancelButton: {
      color: theme.palette.error.main
    },
    alertContainer: {
      marginTop: theme.spacing(2),
      marginBottom: `-${theme.spacing()}px`,
      width: '100%'
    }
  }),
  { name: 'DealHeader' }
)

export interface DealHeaderProps {
  classes?: StyleClasses<typeof useStyles>
  item: Deal & {
    address: string
    city: string
    province: string
    country: string
    postal: string
    phone_number: string
    email: string
    preferred_contact_method: string
  }
  actions: { key: string; label: string; value: string }[]
  warning?: string
  canCreateQuotes?: boolean
  canCreateInvoices?: boolean
  hideDisabledActions?: boolean
  onOwnerChange: (estimatorOption: DashboardEstimator) => void
  handleNewClick: (vals: { type: string; key?: string }) => void
  handleSaveName?: (name: string) => Promise<boolean>
  handleAction: (action: string) => void
  roles?: string[]
  warningOnClose?: () => void
  production: boolean
  canCreateEvent: boolean
  canCreatePayment: boolean
}

function DealHeader(props: DealHeaderProps) {
  const classes = useStyles(props)
  const {
    item,
    onOwnerChange,
    handleNewClick,
    handleSaveName,
    actions,
    handleAction,
    warning,
    canCreateQuotes,
    canCreateInvoices,
    hideDisabledActions = false,
    roles = null,
    warningOnClose,
    production,
    canCreateEvent,
    canCreatePayment
  } = props
  const { isCrew } = useUser()
  const isSm = useMediaQuery('sm')
  const { online } = useConnection()
  const isPrint = useMediaQuery('print')
  const { enqueueSnackbar } = useSnackbar()
  const isLg = !isSm || isPrint

  const [isEditingName, setIsEditingName] = useState(false)
  const [newName, setNewName] = useState(item.name)
  const [loading, setLoading] = useState(false)

  async function handleSaveNameClick() {
    if (newName.length > 50) {
      enqueueSnackbar('Name cannot be more than 50 characters', {
        variant: 'error'
      })
    } else if (newName.length === 0) {
      enqueueSnackbar('Name cannot be empty', {
        variant: 'error'
      })
    } else {
      setLoading(true)
      const res = await handleSaveName(newName)

      if (res) {
        setIsEditingName(false)
      }
      setLoading(false)
    }
  }
  return (
    <div className={classes.root}>
      {isLg && (
        <div className={classes.ownedHeaderLg}>
          <div style={{ width: isEditingName ? '100%' : 'auto' }}>
            <div className={classes.nameDiv} onClick={isEditingName ? null : () => setIsEditingName(true)}>
              {isEditingName ? (
                <div className={classes.nameInputDiv}>
                  <InputField
                    name="name"
                    label=""
                    showErrorBeforeValidation
                    error={newName.length === 0}
                    fullWidth
                    autoFocus
                    defaultValue={item.name}
                    onChange={(e) => {
                      const value = e?.target?.value ?? ''
                      if (value.length === 51) {
                        enqueueSnackbar('Name cannot be more than 50 characters', {
                          variant: 'error'
                        })
                      }
                      setNewName(value)
                    }}
                    disabled={loading}
                  />
                  <IconButton
                    color="inherit"
                    aria-label="Cancel Edit"
                    disabled={loading}
                    loading={loading}
                    onClick={() => setIsEditingName(false)}
                    className={classes.cancelButton}
                  >
                    <CloseIcon />
                  </IconButton>
                  {!loading && (
                    <IconButton
                      color="inherit"
                      aria-label="Save Edit"
                      onClick={handleSaveNameClick}
                      className={classes.successButton}
                    >
                      <CheckIcon />
                    </IconButton>
                  )}
                </div>
              ) : (
                <>
                  <Typography variant="h1" color="textPrimary" className={classes.titleTextLargeScreen}>
                    {`${item.name}`}
                  </Typography>
                  <CreateIcon className={classes.editIcon} />
                </>
              )}
            </div>
          </div>

          <div className={classes.actionButton}>
            <ActionButton
              data-testid="actions"
              actions={actions}
              disabled={actions.length === 0}
              onActionClick={(_ev, action) => handleAction(action)}
            />
            <div className={classes.ownedDropdown}>
              <EstimatorSelect
                popperPlacement="bottom-end"
                name="estimators"
                customOwnedByLabel="Owned By:"
                label=""
                fullWidth
                roles={roles}
                variant="single"
                searchable
                value={item?.owner?.id ? addAuth0Prefix(item.owner.id) : null}
                dealView
                userFields={['user_metadata.playerName', 'picture', 'user_metadata.customAvatar.value']}
                disabled={!online || isCrew || actions.length === 0}
                hideIcon={!online || isCrew || actions.length === 0}
                onChange={async (estimatorOption: DashboardEstimator) => {
                  // set as item owner and save contact only if new
                  onOwnerChange(estimatorOption)
                }}
                autoApply
                showCollapsibleChips
              />
            </div>
          </div>
        </div>
      )}
      {!isLg && (
        <div className={classes.ownedHeaderSm}>
          <div className={classes.actionContainer}>
            <IconButton onClick={() => window.history.back()}>
              <ArrowBackIcon classes={{ root: classes.backLink }} />
            </IconButton>

            {!isCrew && (
              <ActionButton
                data-testid="actions"
                actions={actions}
                disabled={actions.length === 0}
                onActionClick={(_ev, action) => handleAction(action)}
              />
            )}
          </div>
          <div className={classes.headerContainer}>
            <div className={classes.editHeader}>
              <Typography variant="h1" color="textPrimary" align="center">
                {`${item.name}`}
              </Typography>
            </div>
            <Typography variant="h3" color="textSecondary">
              {item.contact.company}
            </Typography>
          </div>
          {warning && (
            <div className={classes.alertContainer}>
              <Alert severity={'warning'} variant={'default'} content={warning} onClose={warningOnClose} />
            </div>
          )}
          <ContactBar
            production={production}
            canCreateInvoices={canCreateInvoices}
            canCreateQuotes={canCreateQuotes}
            hideDisabledActions={hideDisabledActions}
            canCreatePayment={canCreatePayment}
            canCreateEvent={canCreateEvent}
            type={'deal'}
            handleNewClick={handleNewClick}
            item={item}
          />
        </div>
      )}
    </div>
  )
}

export default DealHeader
